import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { DocumentDto } from '@app/generated/model';
import { WorkOrderControllerService } from '@app/generated/service';
import { NetworkStatusService } from '@app/core/services/network-status.service';
import {
  DOCUMENT_CATEGORIES,
  DocumentCategory,
  DocumentDownloadSelection,
  DownloadStatus,
  getDocumentCategory,
  normalizeDocumentCategories,
} from '@app/core/model/document-download';
import {
  OfflineDownloadService,
  OfflineDownloadState,
} from '@app/work-order/offline-download/offline-download.service';
import { formatFileSize } from '@app/utils';
import { FeatureFlagService } from '@app/core/services/feature-flag.service';
import { FeatureFlagKey } from '@dwo/enums';

export interface OfflineDownloadDialogData {
  workOrderId: string;
  workOrderNo?: string;
  projectId?: string;
  downloaded?: boolean;
  initialSelection: DocumentDownloadSelection | null;
}

interface DocumentRow {
  document: DocumentDto;
  category: DocumentCategory;
}

const CATEGORY_LABEL_KEYS: Record<DocumentCategory, string> = {
  JOB_PACKAGE: 'offlineDownload.options.jobPackage',
  ATTACHMENT: 'offlineDownload.options.attachments',
  DOC: 'offlineDownload.options.docs',
  MODEL_3D: 'offlineDownload.options.model3d',
  VIDEO: 'offlineDownload.options.videos',
};

const CATEGORY_BADGE_KEYS: Record<DocumentCategory, string> = {
  JOB_PACKAGE: 'workOrderDocuments.jobPackage',
  ATTACHMENT: 'workOrderDocuments.attachment',
  DOC: 'workOrderDocuments.timeDrawing',
  MODEL_3D: 'offlineDownload.badges.model3d',
  VIDEO: 'offlineDownload.badges.video',
};

const STATUS_ICONS: Record<DownloadStatus, string> = {
  queued: 'schedule',
  downloading: '',
  done: 'check_circle',
  failed: 'error',
};

@Component({
  selector: 'app-offline-download-dialog',
  templateUrl: './offline-download-dialog.component.html',
  styleUrls: ['./offline-download-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OfflineDownloadDialogComponent implements OnInit, OnDestroy {
  // The Doc option is hidden when LINK documents are not shown (ShowTimeDocuments flag).
  visibleCategories: DocumentCategory[] = DOCUMENT_CATEGORIES;
  categoryOptions = this.toCategoryOptions(DOCUMENT_CATEGORIES);

  loading = true;
  loadFailed = false;
  isOnline = navigator.onLine;
  rows: DocumentRow[] = [];
  categories = new Set<DocumentCategory>(DOCUMENT_CATEGORIES);
  specificIds = new Set<string>();
  state: OfflineDownloadState = { running: false, metadata: null, documents: {} };

  private downloadedSelection: DocumentDownloadSelection | null = null;
  private readonly subscription = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OfflineDownloadDialogData,
    public dialogRef: MatDialogRef<OfflineDownloadDialogComponent>,
    private workOrderController: WorkOrderControllerService,
    private offlineDownloadService: OfflineDownloadService,
    private networkStatusService: NetworkStatusService,
    private featureFlagService: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    const showTimeDocuments = this.featureFlagService.isEnabled(FeatureFlagKey.ShowTimeDocuments, this.data.projectId);
    if (!showTimeDocuments) {
      this.visibleCategories = DOCUMENT_CATEGORIES.filter((category) => category !== 'DOC');
      this.categoryOptions = this.toCategoryOptions(this.visibleCategories);
    }

    if (this.data.initialSelection) {
      this.categories = new Set(normalizeDocumentCategories(this.data.initialSelection.categories));
      this.specificIds = new Set(this.data.initialSelection.documentIds);
    }

    if (this.data.downloaded) {
      this.downloadedSelection = this.normalizeSelection(this.data.initialSelection);
    }

    this.subscription.add(
      this.offlineDownloadService.state$(this.data.workOrderId).subscribe((state) => {
        const completedCurrentDownload = this.state.running && !state.running && state.metadata === 'done';
        const hasFailedDocuments = Object.values(state.documents).includes('failed');
        this.state = state;

        if (state.running || state.metadata === 'failed' || hasFailedDocuments) {
          this.downloadedSelection = null;
        } else if (completedCurrentDownload) {
          this.downloadedSelection = this.normalizeSelection(this.selection);
        } else if (state.metadata === 'done' && !this.downloadedSelection) {
          this.downloadedSelection = this.normalizeSelection(this.data.initialSelection);
        }
      }),
    );
    this.subscription.add(this.networkStatusService.isOnline().subscribe((online) => (this.isOnline = online)));
    this.subscription.add(
      this.workOrderController.getWorkOrderDocuments(this.data.workOrderId).subscribe({
        next: (documents) => {
          // The global HTTP error interceptor can re-emit failures as values instead of errors.
          this.loadFailed = !Array.isArray(documents);
          this.rows = Array.isArray(documents)
            ? documents
                .filter((document) => showTimeDocuments || document.documentType !== DocumentDto.DocumentTypeEnum.Link)
                .map((document) => ({ document, category: getDocumentCategory(document) }))
                // Stable sort: group files by category while keeping the API order within each group.
                .sort((a, b) => DOCUMENT_CATEGORIES.indexOf(a.category) - DOCUMENT_CATEGORIES.indexOf(b.category))
            : [];
          this.loading = false;
        },
        error: (error) => {
          console.warn('[OFFLINE_DOWNLOAD] Failed to load documents', error);
          this.loadFailed = true;
          this.loading = false;
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get allFilesSelected(): boolean {
    return this.visibleCategories.every((category) => this.categories.has(category));
  }

  get someFilesSelected(): boolean {
    return this.visibleCategories.some((category) => this.categories.has(category));
  }

  get selectedRows(): DocumentRow[] {
    return this.rows.filter((row) => this.isSelected(row));
  }

  get selectedKnownSize(): string {
    const bytes = this.selectedRows.reduce((total, row) => total + (row.document.fileSize ?? 0), 0);
    return formatFileSize(bytes);
  }

  get selectedUnknownSizeCount(): number {
    return this.selectedRows.filter((row) => row.document.fileSize == null).length;
  }

  get canDownload(): boolean {
    return this.isOnline && !this.loading && !this.loadFailed && !this.state.running;
  }

  get selectionAlreadyDownloaded(): boolean {
    if (!this.downloadedSelection || this.state.running) {
      return false;
    }

    const current = this.normalizeSelection(this.selection);
    return (
      current.categories.length === this.downloadedSelection.categories.length &&
      current.categories.every((category, index) => category === this.downloadedSelection?.categories[index]) &&
      current.documentIds.length === this.downloadedSelection.documentIds.length &&
      current.documentIds.every((id, index) => id === this.downloadedSelection?.documentIds[index])
    );
  }

  get progress(): { done: number; failed: number; total: number } {
    const statuses = Object.values(this.state.documents);
    return {
      done: statuses.filter((status) => status === 'done').length,
      failed: statuses.filter((status) => status === 'failed').length,
      total: statuses.length,
    };
  }

  get downloadPhase(): 'idle' | 'running' | 'done' | 'doneWithFailures' | 'failed' {
    if (this.state.running) {
      return 'running';
    }
    if (this.state.metadata === 'failed') {
      return 'failed';
    }
    if (this.state.metadata === 'done') {
      return this.progress.failed > 0 ? 'doneWithFailures' : 'done';
    }
    return 'idle';
  }

  get selection(): DocumentDownloadSelection {
    return {
      categories: DOCUMENT_CATEGORIES.filter((category) => this.categories.has(category)),
      documentIds: this.rows
        .filter((row) => this.specificIds.has(row.document.id ?? '') && !this.isCoveredByCategory(row))
        .map((row) => row.document.id ?? ''),
    };
  }

  toggleAllFiles(checked: boolean): void {
    this.categories = new Set(checked ? DOCUMENT_CATEGORIES : []);
  }

  toggleCategory(category: DocumentCategory, checked: boolean): void {
    const categories = new Set(this.categories);
    if (checked) {
      categories.add(category);
    } else {
      categories.delete(category);
    }
    this.categories = categories;
  }

  toggleDocument(row: DocumentRow, checked: boolean): void {
    const ids = new Set(this.specificIds);
    const id = row.document.id ?? '';
    if (checked) {
      ids.add(id);
    } else {
      ids.delete(id);
    }
    this.specificIds = ids;
  }

  isCoveredByCategory(row: DocumentRow): boolean {
    return this.categories.has(row.category);
  }

  isRowDisabled(row: DocumentRow): boolean {
    return this.state.running || this.isCoveredByCategory(row);
  }

  toggleRow(row: DocumentRow): void {
    if (!this.isRowDisabled(row)) {
      this.toggleDocument(row, !this.isSelected(row));
    }
  }

  isSelected(row: DocumentRow): boolean {
    return this.isCoveredByCategory(row) || this.specificIds.has(row.document.id ?? '');
  }

  badgeKey(row: DocumentRow): string {
    return CATEGORY_BADGE_KEYS[row.category];
  }

  fileSize(row: DocumentRow): string {
    return row.document.fileSize == null ? '–' : formatFileSize(row.document.fileSize);
  }

  statusOf(row: DocumentRow): DownloadStatus | null {
    return this.state.documents[row.document.id ?? ''] ?? null;
  }

  statusIcon(status: DownloadStatus | null): string {
    return status ? STATUS_ICONS[status] : '';
  }

  download(): void {
    if (!this.canDownload) {
      return;
    }
    void this.offlineDownloadService.download(this.data.workOrderId, this.selection);
  }

  close(): void {
    this.dialogRef.close();
  }

  private toCategoryOptions(categories: DocumentCategory[]) {
    return categories.map((category) => ({ category, labelKey: CATEGORY_LABEL_KEYS[category] }));
  }

  private normalizeSelection(selection: DocumentDownloadSelection | null): DocumentDownloadSelection {
    return {
      categories: [...normalizeDocumentCategories(selection?.categories ?? DOCUMENT_CATEGORIES)].sort(),
      documentIds: [...(selection?.documentIds ?? [])].sort(),
    };
  }
}
